import { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import Layout from "../components/app/Layout";
import api, { API, formatError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { ShieldCheck, Lock, Unlock, Download, Send, AlertCircle } from "lucide-react";

const STATUS_MAP = {
    awaiting_deposit: { label: "Esperando seña", color: "#FBBF24" },
    deposit_uploaded: { label: "Seña en revisión", color: "#FBBF24" },
    active: { label: "Activo", color: "#C9A961" },
    cancelled: { label: "Cancelado", color: "#EF4444" },
};

export default function DealPage() {
    const { id } = useParams();
    const { user } = useAuth();
    const [deal, setDeal] = useState(null);
    const [messages, setMessages] = useState([]);
    const [text, setText] = useState("");
    const [sending, setSending] = useState(false);
    const [showDepositModal, setShowDepositModal] = useState(false);
    const bottomRef = useRef(null);

    const refresh = async () => {
        const d = await api.get(`/deals/${id}`);
        setDeal(d.data);
        const m = await api.get(`/deals/${id}/messages`);
        setMessages(m.data);
    };

    useEffect(() => {
        if (!user) return;
        refresh().catch((e) => toast.error(formatError(e)));
        const t = setInterval(() => { refresh().catch(() => {}); }, 5000);
        return () => clearInterval(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id, user]);

    useEffect(() => {
        bottomRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [messages]);

    if (!user) return <Layout><div className="px-6 py-32 text-center"><p>Iniciá sesión para ver este contrato.</p><Link to="/login" className="btn-gold mt-6 inline-flex">Login</Link></div></Layout>;
    if (!deal) return <Layout><div className="px-6 py-32 text-[#71717A]">Cargando contrato...</div></Layout>;

    const status = STATUS_MAP[deal.status] || STATUS_MAP.awaiting_deposit;
    const sealed = deal.status === "active";
    const canConfirmDeposit = user.role === "winery" && deal.status !== "active";

    const send = async (e) => {
        e.preventDefault();
        if (!text.trim()) return;
        setSending(true);
        try {
            await api.post(`/deals/${id}/messages`, { text });
            setText("");
            refresh();
        } catch (err) {
            toast.error(formatError(err));
        } finally {
            setSending(false);
        }
    };

    return (
        <Layout>
            <div className="px-6 md:px-12 lg:px-16 pt-10 pb-32 grid lg:grid-cols-[1fr_400px] gap-8">
                {/* Contract summary */}
                <div data-testid="deal-summary">
                    <p className="overline">Contrato #{deal.id.slice(0, 8)}</p>
                    <h1 className="font-display text-4xl md:text-5xl mt-3">{deal.wine_name}</h1>
                    <p className="text-sm text-[#A1A1AA] mt-2">{deal.winery_name} · {deal.importer_company} ({deal.importer_country})</p>

                    <div className="flex items-center gap-2 mt-4">
                        <span className="text-xs uppercase tracking-wide" style={{ color: status.color }}>{status.label}</span>
                        {sealed ? <Unlock className="w-4 h-4 text-[#C9A961]" /> : <Lock className="w-4 h-4 text-[#FBBF24]" />}
                    </div>

                    <div className="grid grid-cols-2 gap-4 mt-10">
                        <Stat label="Cajas" value={deal.quantity_cases} />
                        <Stat label="Botellas" value={(deal.quantity_cases * 12).toLocaleString()} />
                        <Stat label="FOB / botella" value={`USD ${deal.price_per_bottle_usd.toFixed(2)}`} />
                        <Stat label="Total FOB" value={`USD ${deal.total_fob_usd.toFixed(2)}`} bold />
                        <Stat label="Seña 30%" value={`USD ${deal.deposit_usd.toFixed(2)}`} gold />
                        <Stat label="Comisión CellarGlobal 5%" value={`USD ${deal.commission_usd.toFixed(2)}`} />
                    </div>

                    <a
                        href={`${API}/deals/${id}/contract.pdf`}
                        target="_blank"
                        rel="noreferrer"
                        className="btn-outline mt-10 inline-flex"
                        data-testid="deal-pdf-link"
                    >
                        <Download className="w-4 h-4" /> Descargar Contrato PDF
                    </a>

                    {/* Deposit instructions / confirm */}
                    <div className="mt-12 border border-[#C9A961] p-8" data-testid="deal-deposit-block">
                        <div className="flex items-center gap-3 mb-4">
                            <ShieldCheck className="w-5 h-5 text-[#C9A961]" />
                            <p className="overline">Instrucciones de seña</p>
                        </div>
                        {!sealed ? (
                            <>
                                <p className="text-base text-[#E4E4E7] leading-relaxed">
                                    Para reservar el container, el exportador transfiere <b className="text-[#C9A961]">USD {deal.deposit_usd.toFixed(2)} (30%)</b> directo
                                    a la cuenta bancaria de la bodega. Datos bancarios cargados por la bodega.
                                    Una vez transferido, la bodega sube la captura del comprobante para activar el contrato
                                    y desbloquear el contacto directo.
                                </p>
                                {deal.bank_details && (
                                    <div className="mt-6 bg-black p-4 border border-[#27272A] text-sm font-mono whitespace-pre-wrap">{deal.bank_details}</div>
                                )}
                                {canConfirmDeposit && (
                                    <button onClick={() => setShowDepositModal(true)} className="btn-gold mt-6" data-testid="deal-confirm-deposit">
                                        Confirmar Seña Recibida (Bodega)
                                    </button>
                                )}
                            </>
                        ) : (
                            <p className="text-base text-[#C9A961]">
                                ✓ Seña confirmada. Contrato activo. Contacto directo desbloqueado para coordinar embarque.
                            </p>
                        )}
                    </div>
                </div>

                {/* Chat */}
                <aside className="border border-[#27272A] flex flex-col h-[600px] lg:h-[700px]" data-testid="deal-chat">
                    <div className="px-5 py-4 border-b border-[#27272A] flex items-center justify-between">
                        <div>
                            <p className="overline">Chat interno</p>
                            <p className="text-xs text-[#71717A] mt-1">{sealed ? "Datos desbloqueados" : "Contactos personales bloqueados"}</p>
                        </div>
                        {sealed ? <Unlock className="w-4 h-4 text-[#C9A961]" /> : <Lock className="w-4 h-4 text-[#FBBF24]" />}
                    </div>

                    <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
                        {messages.map((m) => (
                            <MessageBubble key={m.id} msg={m} me={user.id} />
                        ))}
                        <div ref={bottomRef} />
                    </div>

                    <form onSubmit={send} className="border-t border-[#27272A] p-4 flex gap-2">
                        <input
                            className="input-line flex-1"
                            placeholder={sealed ? "Escribí un mensaje" : "Escribí (sin datos personales)"}
                            value={text}
                            onChange={(e) => setText(e.target.value)}
                            data-testid="deal-msg-input"
                        />
                        <button type="submit" disabled={sending || !text.trim()} className="btn-gold !py-2 !px-4" data-testid="deal-msg-send">
                            <Send className="w-4 h-4" />
                        </button>
                    </form>
                </aside>
            </div>

            {showDepositModal && (
                <DepositConfirmModal
                    deal={deal}
                    onClose={() => setShowDepositModal(false)}
                    onDone={() => { setShowDepositModal(false); refresh(); }}
                />
            )}
        </Layout>
    );
}

function MessageBubble({ msg, me }) {
    const isSystem = msg.sender_role === "system";
    const mine = msg.sender_user_id === me;
    if (isSystem) {
        return (
            <div className="flex items-start gap-2 text-xs text-[#C9A961] bg-[#0A0A0A] border border-[#27272A] p-3" data-testid={`msg-system-${msg.id}`}>
                <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
                <div>
                    <p className="font-semibold mb-1">{msg.sender_name}</p>
                    <p className="text-[#E4E4E7] leading-relaxed">{msg.text}</p>
                </div>
            </div>
        );
    }
    return (
        <div className={`flex flex-col ${mine ? "items-end" : "items-start"}`} data-testid={`msg-${msg.id}`}>
            <div className={`max-w-[85%] px-4 py-2.5 ${mine ? "bg-[#C9A961] text-black" : "bg-[#0A0A0A] border border-[#27272A] text-white"}`}>
                <p className="text-xs opacity-60 mb-1">{msg.sender_name} · {msg.sender_role}</p>
                <p className="text-sm leading-relaxed whitespace-pre-wrap">{msg.text}</p>
            </div>
            {msg.was_redacted && <p className="text-xs text-[#FBBF24] mt-1">⚠️ Datos personales bloqueados</p>}
        </div>
    );
}

function Stat({ label, value, gold, bold }) {
    return (
        <div className="border border-[#27272A] p-4">
            <p className="text-xs text-[#71717A] uppercase tracking-wide">{label}</p>
            <p className={`mt-1 ${bold ? "font-display text-2xl" : ""} ${gold ? "text-[#C9A961]" : "text-white"}`}>{value}</p>
        </div>
    );
}

function DepositConfirmModal({ deal, onClose, onDone }) {
    const [screenshot, setScreenshot] = useState("");
    const [bank, setBank] = useState(deal.bank_details || "");
    const [busy, setBusy] = useState(false);
    const confirm = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            await api.post(`/deals/${deal.id}/deposit`, { screenshot_url: screenshot, bank_details: bank });
            toast.success("Seña confirmada. Contrato activado.");
            onDone();
        } catch (err) {
            toast.error(formatError(err));
        } finally { setBusy(false); }
    };
    return (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
            <form onSubmit={confirm} className="bg-[#0A0A0A] border border-[#27272A] max-w-lg w-full" data-testid="deposit-modal">
                <div className="p-8 border-b border-[#27272A]">
                    <p className="overline">Confirmar Seña</p>
                    <h3 className="font-display text-2xl mt-2">USD {deal.deposit_usd.toFixed(2)} recibidos</h3>
                </div>
                <div className="p-8 space-y-6">
                    <div>
                        <label className="overline block mb-2">URL de captura de transferencia</label>
                        <input required className="input-line" placeholder="https://..." value={screenshot} onChange={(e) => setScreenshot(e.target.value)} data-testid="deposit-screenshot" />
                    </div>
                    <div>
                        <label className="overline block mb-2">Datos bancarios (visible al exportador)</label>
                        <textarea className="input-line resize-none" rows="3" value={bank} onChange={(e) => setBank(e.target.value)} data-testid="deposit-bank" />
                    </div>
                    <div className="flex gap-3">
                        <button type="button" onClick={onClose} className="btn-ghost flex-1">Cancelar</button>
                        <button type="submit" disabled={busy} className="btn-gold flex-1" data-testid="deposit-submit">{busy ? "Confirmando..." : "Confirmar y Activar"}</button>
                    </div>
                </div>
            </form>
        </div>
    );
}
